import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ClientError } from '~/errors';
import { createLogger } from '~/utils/logging';
import { PERIOD_LIST_ALL_QUERY_KEY } from '~/services/period';
import type { Period } from '~/services/types';
import { useHttpClient } from '~/hooks/useHttpClient';
import { sanitizeTagValue } from '~/utils/instrumentation';

const log = createLogger('usePeriodEditMutation');

export type EditPeriodRequest = {
  periodId: string;
  shortName: string;
  name: string;
  students: {
    id?: string;
    voiceName: string;
  }[];
};

export type EditPeriodResponse = {
  period?: Period; // No period is returned if the period is not modified.
};

export function usePeriodEditMutation() {
  const httpClient = useHttpClient();
  const queryClient = useQueryClient();

  const { data, error, mutate, reset, status } = useMutation({
    mutationFn: async (request: EditPeriodRequest) => {
      log.debug('Editing period', request);
      const { period } = await httpClient.post<EditPeriodResponse>('/period/edit', request);
      if (period) {
        log.info('Edited period successfully', {
          periodId: period.id,
          periodName: sanitizeTagValue(period.name),
          periodShortName: sanitizeTagValue(period.shortName),
        });
      } else {
        log.info('Edited period - no changes were made');
      }
      return { period };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PERIOD_LIST_ALL_QUERY_KEY] });
    },
    onError: (error: Error) => {
      if (error instanceof ClientError) {
        // Ignore client errors.
      } else {
        log.error('Failed to edit period', {
          errorMessage: sanitizeTagValue(error.message),
        });
      }
    },
  });

  return {
    data,
    error,
    mutate,
    reset,
    status,
  };
}
